import { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
// @mui
import { Card, Container, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
// GoogleReCaptcha
import { GoogleReCaptchaProvider, useGoogleReCaptcha, GoogleReCaptcha } from 'react-google-recaptcha-v3';
// routes
import { PATH_AUTH } from '../../routes/paths';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
import useWebView from '../../hooks/useWebView';
import useLocales from '../../hooks/useLocales';
// components
import Image from '../../components/Image';
import Logo from '../../components/Logo';
import Page from '../../components/Page';
// sections
import { LoginForm, LoginWebView } from '../../sections/auth/login';
// utils
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  zIndex: 9,
  lineHeight: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  padding: theme.spacing(3),
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(7, 5, 0, 7),
  },
}));

const SectionStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 464,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  margin: theme.spacing(2, 0, 2, 2),
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

export default function Login() {
  const { method, isAuthenticated } = useAuth();
  const { translate } = useLocales('login');
  const [gRecaptchaSiteKey, setGRecaptchaSiteKey] = useState('');

  const { isWebView } = useWebView();

  const smUp = useResponsive('up', 'sm');

  const mdUp = useResponsive('up', 'md');

  const getCapchaKey = async () => {
    const configsResponse = await axios.get('api/v5/commons/configs/');
    const _gRecaptchaSiteKey = configsResponse.data.g_recaptcha_site_key;
    setGRecaptchaSiteKey(_gRecaptchaSiteKey);
  };

  useEffect(() => {
    getCapchaKey();
  }, []);

  if (isAuthenticated) {
    return <Navigate replace to="/dashboard" />;
  }

  return (
    <Page title="Đăng nhập">
      <RootStyle>
        <HeaderStyle>
          <Logo />
        </HeaderStyle>

        {mdUp && (
          <SectionStyle>
            <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
              {translate('main_title')}
            </Typography>
            <Image
              visibleByDefault
              disabledEffect
              alt="register"
              src="/assets/illustrations/illustration_register.png"
            />
          </SectionStyle>
        )}

        <Container maxWidth="sm">
          <ContentStyle>
            {gRecaptchaSiteKey && (
              <GoogleReCaptchaProvider reCaptchaKey={gRecaptchaSiteKey}>
                {!isWebView ? <LoginForm /> : <LoginWebView />}
              </GoogleReCaptchaProvider>
            )}
          </ContentStyle>
        </Container>
      </RootStyle>
    </Page>
  );
}
